import React, { useEffect, useState } from 'react';
import { Select, Button, Spinner, Toast } from '../../lib';
import { useNavigate } from 'react-router-dom';
import apiServices from '../../services/apiServices';
import {
  getJobsForSteward,
  getStewDetailRecordsForJob,
  saveStewHeaderReq,
  addMemberToStewReport,
  removeMemberFromStewReport,
  saveStewDetailRecs,
  job,
  StewardReportDetail_ui,
  saveStewDetailRecs as SaveStewDetailRecsReq,
  saveStewHeaderReq as SaveStewHeaderReq,
  addMemberToStewReport as AddMemberToStewReportReq,
  removeMemberFromStewReport as RemoveMemberFromStewReportReq,
  getStewDetailRecordsForJob as GetStewDetailRecordsForJobReq,
  getJobsForSteward as GetJobsForStewardReq,
  searchMember as SearchMemberReq,
} from '../../services/dtos';

const StewardReportPage: React.FC = () => {
  const [jobs, setJobs] = useState<job[]>([]);
  const [selectedJob, setSelectedJob] = useState<job | null>(null);
  const [reportDate, setReportDate] = useState<string>('');
  const [details, setDetails] = useState<StewardReportDetail_ui[]>([]);
  const [header, setHeader] = useState<any>(null); // TODO: type header
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();
  const client = apiServices.client;
  const [otherRate1, setOtherRate1] = useState<number>(0);
  const [otherRate2, setOtherRate2] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<StewardReportDetail_ui[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<{ [id: string]: boolean }>({});

  const toggleExpand = (id: string) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDetailFieldChange = (index: number, field: string, value: any) => {
    setDetails(prev => {
      const updated = [...prev];
      const detail = { ...updated[index], [field]: value };
      updated[index] = detail;
      return updated;
    });
  };

  // Step 1: Load jobs for steward
  useEffect(() => {
    setLoading(true);
    client
      .get(new GetJobsForStewardReq())
      .then((data: job[]) => {
        setJobs(data);
        setLoading(false);
      })
      .catch((e) => {
        setError('Failed to load jobs');
        setLoading(false);
      });
  }, []);

  // Step 2: Load or initialize report details for selected job/date
  const loadReportDetails = () => {
    if (!selectedJob || !reportDate) return;
    setLoading(true);
    const req = new GetStewDetailRecordsForJobReq();
    req.job_id = selectedJob.job_ID;
    req.report_date = reportDate;
    client
      .get(req)
      .then((data: StewardReportDetail_ui[]) => {
        setDetails(data);
        setLoading(false);
        setError(null);
      })
      .catch((e) => {
        setError('Failed to load steward report details');
        setLoading(false);
      });
  };

  // Update header state when details are loaded
  useEffect(() => {
    if (details && details.length > 0) {
      // Try to get headerId from first detail record
      setHeader({ stewardReportHeaderId: details[0].stewardReportHeaderId });
    }
  }, [details]);

  // Add useEffect to trigger loadReportDetails when selectedJob or reportDate changes
  useEffect(() => {
    if (selectedJob && reportDate) {
      loadReportDetails();
    }
  }, [selectedJob, reportDate]);

  // Step 3: Save header info
  const saveHeader = (headerData: any) => {
    setLoading(true);
    const req = new SaveStewHeaderReq();
    Object.assign(req, headerData);
    client
      .put(req)
      .then(() => {
        setSuccess('Header updated');
        setLoading(false);
      })
      .catch((e) => {
        setError('Failed to update header');
        setLoading(false);
      });
  };

  // Step 4: Add member
  const addMember = (unique_member_id: string) => {
    setLoading(true);
    const req = new AddMemberToStewReportReq();
    req.unique_member_id = unique_member_id;
    req.stewardReportHeaderId = header?.stewardReportHeaderId;
    req.job_ID = selectedJob?.job_ID;
    client
      .post(req)
      .then(() => {
        setSuccess('Member added');
        loadReportDetails();
        setSearchTerm('');
        setSearchResults([]);
      })
      .catch((e) => {
        setError('Failed to add member');
        setLoading(false);
      });
  };

  // Step 4: Remove member
  const removeMember = (id: number) => {
    setLoading(true);
    const req = new RemoveMemberFromStewReportReq();
    req.id = id;
    client
      .delete(req)
      .then(() => {
        setSuccess('Member removed');
        loadReportDetails();
      })
      .catch((e) => {
        setError('Failed to remove member');
        setLoading(false);
      });
  };

  // Step 5: Save detail records
  const saveDetails = (detailRecords: StewardReportDetail_ui[], final_submit = false) => {
    setLoading(true);
    const req = new SaveStewDetailRecsReq();
    req.detail_records = detailRecords;
    req.final_submit = final_submit;
    client
      .put(req)
      .then(() => {
        setSuccess(final_submit ? 'Report submitted!' : 'Details saved');
        setLoading(false);
        if (final_submit) {
          // Optionally redirect or lock UI
        }
      })
      .catch((e) => {
        setError('Failed to save details');
        setLoading(false);
      });
  };

  const searchMembers = () => {
    if (!searchTerm) return;
    setSearchLoading(true);
    const req = new SearchMemberReq();
    req.search_term = searchTerm;
    client
      .get(req)
      .then((data: StewardReportDetail_ui[]) => {
        setSearchResults(data);
        setSearchLoading(false);
      })
      .catch(() => {
        setSearchResults([]);
        setSearchLoading(false);
      });
  };

  // Helper to calculate total hours from in/out times
  function calculateTotalHours(detail: StewardReportDetail_ui): number {
    const parse = (t?: string) => {
      if (!t) return null;
      const [h, m] = t.split(":").map(Number);
      return h * 60 + m;
    };
    let total = 0;
    for (let i = 1; i <= 3; i++) {
      const inTime = parse((detail as any)[`in_time_${i}`]);
      const outTime = parse((detail as any)[`out_time_${i}`]);
      if (inTime !== null && outTime !== null && outTime > inTime) {
        total += (outTime - inTime) / 60;
      }
    }
    return Math.round(total * 100) / 100;
  }

  // Helper to validate HH:MM 24hr format
  function isValidTime(value: string) {
    return value === '' || /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
  }

  function isTimePairMissing(inTime: string, outTime: string) {
    return (inTime && !outTime) || (!inTime && outTime);
  }

  function isOutTimeAfterInTime(inTime: string, outTime: string) {
    if (!isValidTime(inTime) || !isValidTime(outTime)) return true;
    if (!inTime || !outTime) return true;
    const [inH, inM] = inTime.split(":").map(Number);
    const [outH, outM] = outTime.split(":").map(Number);
    return (outH * 60 + outM) - (inH * 60 + inM) >= 1;
  }

  // Handler for time changes (with HH:MM validation)
  const handleTimeChange = (index: number, field: string, value: string) => {
    setDetails(prev => {
      const updated = [...prev];
      const detail = { ...updated[index], [field]: value };
      // Only recalculate total hours if all entered times are valid or empty
      const valid = value === '' || /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
      if (valid) {
        detail.qty_hrs_ttl = calculateTotalHours(detail);
      }
      updated[index] = detail;
      return updated;
    });
  };

  // Helper to check if all non-blank time entries are valid
  function allTimesValid(details: StewardReportDetail_ui[]): boolean {
    for (const detail of details) {
      for (let i = 1; i <= 3; i++) {
        const inTime = (detail as any)[`in_time_${i}`] || '';
        const outTime = (detail as any)[`out_time_${i}`] || '';
        // If either is entered, both must be entered and valid
        if ((inTime && !outTime) || (!inTime && outTime)) {
          return false;
        }
        if ((inTime && !isValidTime(inTime)) || (outTime && !isValidTime(outTime))) {
          return false;
        }
        // If both are present, outTime must be at least 1 minute after inTime
        if (inTime && outTime && (!isOutTimeAfterInTime(inTime, outTime))) {
          return false;
        }
      }
    }
    return true;
  }

  // UI rendering
  return (
    <div className="mx-auto w-full flex flex-col gap-8 dark:text-white">
      <h2 className="text-2xl font-bold">Steward Report</h2>
      {error && <Toast color="failure">{error}</Toast>}
      {success && <Toast color="success">{success}</Toast>}
      {loading && <Spinner color="info" />}
      {/* Step 1: Job selection */}
      <div>
        <label className="block font-semibold mb-2">Select Job:</label>
        <Select
          value={selectedJob?.job_ID || ''}
          onChange={e => {
            const job = jobs.find(j => j.job_ID === e.target.value);
            setSelectedJob(job || null);
          }}
        >
          <option value="">Choose a job</option>
          {jobs.map(job => (
            <option key={job.job_ID} value={job.job_ID}>{job.show} ({job.start_Date})</option>
          ))}
        </Select>
      </div>
      {/* Step 2: Date selection */}
      {selectedJob && (
        <div>
          <label className="block font-semibold mb-2">Report Date:</label>
          <input
            type="date"
            value={reportDate}
            onChange={e => {
              setReportDate(e.target.value);
              // TODO: If there are unsaved changes, prompt user to save first before switching jobs/dates
              // No direct call to loadReportDetails here
            }}
            className="border rounded px-2 py-1"
          />
          {/* TODO: Ask user to save first if there are unsaved changes before switching jobs/dates */}
        </div>
      )}
      {/* Step 3: Header editing UI */}
      {/* {header && (
        <div className="flex flex-col gap-2 border p-4 rounded bg-gray-50 dark:bg-gray-800">
          <div className="font-semibold mb-2">Report Header</div>
          <div className="flex gap-4 items-center">
            <label htmlFor="otherRate1">Other Rate 1:</label>
            <input
              id="otherRate1"
              type="number"
              value={otherRate1}
              onChange={e => setOtherRate1(Number(e.target.value))}
              className="border rounded px-2 py-1 w-24"
            />
            <label htmlFor="otherRate2">Other Rate 2:</label>
            <input
              id="otherRate2"
              type="number"
              value={otherRate2}
              onChange={e => setOtherRate2(Number(e.target.value))}
              className="border rounded px-2 py-1 w-24"
            />
            <Button
              color="info"
              size="small"
              onClick={() => saveHeader({
                stewardReportHeaderId: header.stewardReportHeaderId,
                other_rate_1: otherRate1,
                other_rate_2: otherRate2
              })}
            >
              Save Header
            </Button>
          </div>
        </div>
      )} */}
      {/* TODO: Re-enable report header section when needed */}
      {/* Step 4: Add/Remove People UI */}
      {header && (
        <div className="flex flex-col gap-2 border p-4 rounded bg-gray-50 dark:bg-gray-800 w-full">
          <div className="font-semibold mb-2">Add/Remove Members</div>
          <div className="flex gap-2 items-center mb-2">
            <input
              type="text"
              placeholder="Search members by name or ID"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="border rounded px-2 py-1"
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchMembers(); } }}
            />
            <Button color="info" size="small" onClick={searchMembers} disabled={!searchTerm}>
              Search
            </Button>
            {searchLoading && <Spinner color="info" size="sm" />}
          </div>
          {searchResults.length > 0 && (
            <div className="mb-2 relative">
              <div className="font-semibold text-xs mb-1 flex items-center justify-between">
                <span>Search Results:</span>
                <button
                  className="rounded-full w-6 h-6 flex items-center justify-center p-0 text-gray-400 hover:text-red-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  style={{ minWidth: '1.5rem' }}
                  title="Dismiss search results"
                  onClick={() => { setSearchResults([]); setSearchTerm(''); }}
                >
                  ×
                </button>
              </div>
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {searchResults.map(member => {
                  // Try to show FirstName LastName if available
                  const firstName = (member as any).first_name;
                  const lastName = (member as any).last_name;
                  const contactEmail = (member as any).contact_email;
                  let displayName = '';
                  if (firstName || lastName) {
                    displayName = `${firstName || ''} ${lastName || ''}`.trim();
                  } else if (member.member_name) {
                    displayName = member.member_name;
                  } else {
                    displayName = member.unique_member_id;
                  }
                  return (
                    <li key={member.unique_member_id} className="flex flex-col gap-0 py-1">
                      <div className="flex items-center gap-2">
                        <Button
                          color="success"
                          size="xs"
                          className="rounded-full w-6 h-6 flex items-center justify-center p-0 mr-2"
                          style={{ minWidth: '1.5rem' }}
                          onClick={() => addMember(member.unique_member_id)}
                          title="Add"
                        >
                          +
                        </Button>
                        <span>{displayName}</span>
                      </div>
                      {contactEmail && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">{contactEmail}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <div className="font-semibold text-xs mb-1">Current Members:</div>
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {details.map((detail, idx) => {
              const isExpanded = expandedRows[detail.unique_member_id];
              return (
                <li key={detail.unique_member_id} className="flex flex-col gap-1 py-2">
                  <div className="flex items-center gap-2">
                    <Button
                      color="failure"
                      size="xs"
                      className="rounded-full w-6 h-6 flex items-center justify-center p-0 mr-2"
                      style={{ minWidth: '1.5rem' }}
                      onClick={() => removeMember(detail.id)}
                      title="Remove"
                    >
                      ×
                    </Button>
                    <span>{detail.member_name || detail.unique_member_id}</span>
                  </div>
                  <div className="flex gap-4 items-center mt-1">
                    {[1,2,3].map(i => {
                      const inTime = (detail as any)[`in_time_${i}`] || '';
                      const outTime = (detail as any)[`out_time_${i}`] || '';
                      const inInvalid = inTime && !isValidTime(inTime);
                      const outInvalid = outTime && !isValidTime(outTime);
                      const pairMissing = isTimePairMissing(inTime, outTime);
                      const outBeforeIn = inTime && outTime && isValidTime(inTime) && isValidTime(outTime) && !isOutTimeAfterInTime(inTime, outTime);
                      // Compose error message
                      let errorMsg = '';
                      if (inInvalid) errorMsg = 'In time must be HH:MM (24hr)';
                      else if (outInvalid) errorMsg = 'Out time must be HH:MM (24hr)';
                      else if (pairMissing) errorMsg = 'Both In and Out times must be entered';
                      else if (outBeforeIn) errorMsg = 'Out time must be at least 1 minute after In time';
                      return (
                        <div key={i} className="flex flex-col items-start relative" style={{ minHeight: '2.5rem' }}>
                          <div className="flex gap-1 items-center">
                            <label className="text-xs">In {i}:</label>
                            <input
                              type="text"
                              placeholder="HH:MM"
                              value={inTime}
                              onChange={e => handleTimeChange(idx, `in_time_${i}`, e.target.value)}
                              className={`border rounded px-1 py-0.5 w-20 ${
                                (inInvalid || pairMissing || outBeforeIn)
                                  ? 'border-red-500 bg-red-50 dark:bg-red-900'
                                  : ''
                              }`}
                            />
                            <label className="text-xs ml-2">Out {i}:</label>
                            <input
                              type="text"
                              placeholder="HH:MM"
                              value={outTime}
                              onChange={e => handleTimeChange(idx, `out_time_${i}`, e.target.value)}
                              className={`border rounded px-1 py-0.5 w-20 ${
                                (outInvalid || pairMissing || outBeforeIn)
                                  ? 'border-red-500 bg-red-50 dark:bg-red-900'
                                  : ''
                              }`}
                            />
                          </div>
                          {/* Inline error message, absolutely positioned below the input row */}
                          <div style={{ minHeight: '1.2rem', position: 'relative', width: '100%' }}>
                            {errorMsg && (
                              <span className="absolute left-0 text-xs text-red-600 mt-0.5">{errorMsg}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <span className="ml-4 text-xs font-semibold">Total: {detail.qty_hrs_ttl ?? 0} hrs</span>
                    <Button
                      color="info"
                      size="xs"
                      className="rounded-full w-6 h-6 flex items-center justify-center p-0 ml-2"
                      style={{ minWidth: '1.5rem' }}
                      onClick={() => toggleExpand(detail.unique_member_id)}
                      title={isExpanded ? 'Hide details' : 'Show details'}
                    >
                      {isExpanded ? '−' : '+'}
                    </Button>
                  </div>
                  {isExpanded && (
                    <div className="flex flex-wrap gap-4 items-center mt-2 bg-gray-100 dark:bg-gray-900 p-2 rounded">
                      <div className="flex flex-col">
                        <label className="text-xs font-semibold mb-1">Parking ($)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={(detail as any).parking || ''}
                          onChange={e => handleDetailFieldChange(idx, 'parking', e.target.value)}
                          className="border rounded px-2 py-1 w-24"
                          placeholder="0.00"
                        />
                      </div>
                      <div className="flex flex-col">
                        <label className="text-xs font-semibold mb-1">Other Amount ($)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={(detail as any).other_amt || ''}
                          onChange={e => handleDetailFieldChange(idx, 'other_amt', e.target.value)}
                          className="border rounded px-2 py-1 w-24"
                          placeholder="0.00"
                        />
                      </div>
                      <div className="flex flex-col flex-1 min-w-[120px]">
                        <label className="text-xs font-semibold mb-1">Other Note</label>
                        <input
                          type="text"
                          value={(detail as any).other_note || ''}
                          onChange={e => handleDetailFieldChange(idx, 'other_note', e.target.value)}
                          className="border rounded px-2 py-1 w-full"
                          placeholder="Short note"
                          maxLength={200}
                        />
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {/* Step 5: Detail editing (TODO: render editable detail records) */}
      {/* TODO: Render editable table for details, call saveDetails on save */}
      {/* Step 6: Submit (final_submit) */}
      <div className="mt-4">
        <Button
          color="success"
          onClick={() => saveDetails(details, true)}
          disabled={details.length === 0 || !allTimesValid(details)}
          title={
            details.length === 0
              ? 'No members to submit'
              : !allTimesValid(details)
              ? 'All non-blank time entries must be valid and paired (HH:MM, 24hr)'
              : ''
          }
        >
          Submit Report
        </Button>
        {!allTimesValid(details) && (
          <div className="text-xs text-red-600 mt-1">
            All non-blank time entries must be valid (HH:MM, 24hr) and each In/Out pair must be complete.
          </div>
        )}
      </div>
      {/* TODO: Add more UI/UX polish, validation, and error handling as needed */}
    </div>
  );
};

export default StewardReportPage; 